import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDressingChangeFrequency, useUpdateDressingChangeFrequencyMutation } from "../../../../../../features/patients/dressing-change-frequency";
import { getInterventionList, getMinimumDatasetInfo } from "../../../../../../app/patientSlice";
import { useReadOnly } from "../../../../../../hooks/useReadOnly";
import { usePermissions } from "../../../../../../hooks/usePermissions";
import { toLocalISOWithOffset } from "../../../../../../utils/commonHelper";
import { getDefaultSchedule, getNextDueDate, scheduleToApiFields } from "../utils/scheduleUtils";
import type { Schedule, SchedulePatch, DayOfWeek, DressChangeFrequency, UseDressingChangeFrequencyDesignReturn } from "../types";

interface HookParams {
  interventionId: string | number;
}

interface ScheduleDates {
  scheduleAnchorAt: string | null;
  nextDueAt: string | null;
}

const scheduleKey = (type: "primary" | "secondary", index: number): string => `${type}_${index}`;

const schedulesMatch = (left: Schedule, right: Schedule): boolean =>
  left.mode === right.mode &&
  left.every === right.every &&
  left.unit === right.unit &&
  left.times === right.times &&
  left.period === right.period &&
  left.prnMax === right.prnMax &&
  left.prnCap === right.prnCap &&
  left.days.length === right.days.length &&
  left.days.every((day) => right.days.includes(day));

const buildSchedules = (dressChange: DressChangeFrequency): Record<string, Schedule> => {
  const next: Record<string, Schedule> = {};
  (["primary", "secondary"] as const).forEach((type) => {
    const items = dressChange?.data?.[type]?.productCategory ?? [];
    items.forEach((item, index) => {
      next[scheduleKey(type, index)] = getDefaultSchedule(item);
    });
  });
  return next;
};

const getScheduleDates = (schedule: Schedule, scheduleAnchorAt = toLocalISOWithOffset(new Date())): ScheduleDates => {
  if (schedule.mode === "unset" || (schedule.mode === "prn" && !schedule.prnCap)) {
    return { scheduleAnchorAt: null, nextDueAt: null };
  }

  const nextDueDate = getNextDueDate(schedule, new Date(scheduleAnchorAt));
  return { scheduleAnchorAt, nextDueAt: nextDueDate ? toLocalISOWithOffset(nextDueDate) : null };
};

export const useDressingChangeFrequencyDesign = ({ interventionId }: HookParams): UseDressingChangeFrequencyDesignReturn => {
  const dispatch = useDispatch();
  const { patientId, assessmentId } = useParams<{ patientId: string; assessmentId: string }>();
  const { handleSubmit } = useForm();
  const isReadOnly = useReadOnly();
  const { hasPermission } = usePermissions();
  const frequencyParams = useMemo(
    () => ({
      patientId: patientId ?? "",
      assessmentId: assessmentId ?? "",
      interventionId,
    }),
    [assessmentId, interventionId, patientId]
  );
  const { data: frequencyData, isLoading, isRefreshing, refetch } = useDressingChangeFrequency(frequencyParams);
  const { mutateAsync, isPending: isSaving } = useUpdateDressingChangeFrequencyMutation();
  const dressChange = useMemo(() => frequencyData ?? {}, [frequencyData]);

  const [editable, setEditable] = useState(false);
  const [formChangesConfirm, setFormChangesConfirm] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [clickCancel, setClickCancel] = useState(false);
  const [schedules, setSchedules] = useState<Record<string, Schedule>>(() => buildSchedules(dressChange));
  const [changedScheduleKeys, setChangedScheduleKeys] = useState<Set<string>>(new Set());
  const [draftScheduleDates, setDraftScheduleDates] = useState<Record<string, ScheduleDates>>({});

  const confirmationClose = useCallback((): void => {
    setConfirmationModal(false);
    setClickCancel(false);
  }, []);

  const yesConfirmationModalClick = useCallback((): void => {
    setConfirmationModal(false);
    setEditable(false);
    setFormChangesConfirm(false);
    setClickCancel(false);
    setSchedules(buildSchedules(dressChange));
    setDraftScheduleDates({});
  }, [dressChange]);

  const handleEdit = useCallback((): void => {
    setSchedules(buildSchedules(dressChange));
    setFormChangesConfirm(false);
    setClickCancel(false);
    setChangedScheduleKeys(new Set());
    setDraftScheduleDates({});
    setEditable(true);
  }, [dressChange]);

  const handleCancel = useCallback((): void => {
    toast.dismiss("success1");
    setClickCancel(true);
  }, []);

  const updateSchedule = useCallback((type: "primary" | "secondary", index: number, patch: SchedulePatch): void => {
    const key = scheduleKey(type, index);
    const currentSchedule = schedules[key] ?? getDefaultSchedule({});
    const nextSchedule = { ...currentSchedule, ...patch };
    if (schedulesMatch(currentSchedule, nextSchedule)) return;
    const persistedSchedule = getDefaultSchedule(dressChange?.data?.[type]?.productCategory?.[index] ?? {});

    setFormChangesConfirm(true);
    setChangedScheduleKeys((keys) => {
      const nextKeys = new Set(keys);
      if (schedulesMatch(nextSchedule, persistedSchedule)) {
        nextKeys.delete(key);
      } else {
        nextKeys.add(key);
      }
      return nextKeys;
    });
    setDraftScheduleDates((dates) => {
      if (schedulesMatch(nextSchedule, persistedSchedule)) {
        const { [key]: _removed, ...remainingDates } = dates;
        return remainingDates;
      }
      return { ...dates, [key]: getScheduleDates(nextSchedule, dates[key]?.scheduleAnchorAt ?? undefined) };
    });
    setSchedules((prev) => ({
      ...prev,
      [key]: nextSchedule,
    }));
  }, [dressChange, schedules]);

  const toggleWeekday = useCallback(
    (type: "primary" | "secondary", index: number, day: DayOfWeek): void => {
      const current = schedules[scheduleKey(type, index)];
      if (!current) return;
      const days = current.days.includes(day) ? current.days.filter((d) => d !== day) : ([...current.days, day] as DayOfWeek[]);
      updateSchedule(type, index, { days });
    },
    [schedules, updateSchedule]
  );

  const getSchedule = useCallback(
    (type: "primary" | "secondary", index: number): Schedule => schedules[scheduleKey(type, index)] ?? getDefaultSchedule({}),
    [schedules]
  );

  const getDraftScheduleDates = useCallback(
    (type: "primary" | "secondary", index: number): ScheduleDates | undefined => draftScheduleDates[scheduleKey(type, index)],
    [draftScheduleDates]
  );

  const doSubmit = useCallback(async (): Promise<void> => {
    const buildType = (type: "primary" | "secondary") => {
      const items = dressChange?.data?.[type]?.productCategory ?? [];
      const productCategory = items.map((item, index) => {
        const schedule = schedules[scheduleKey(type, index)] ?? getDefaultSchedule(item);
        const apiFields = scheduleToApiFields(schedule);
        const key = scheduleKey(type, index);
        const scheduleChanged = changedScheduleKeys.has(key) && !schedulesMatch(schedule, getDefaultSchedule(item));
        const scheduleDates = scheduleChanged ? draftScheduleDates[key] ?? {} : {};
        return {
          ...item,
          ...apiFields,
          ...scheduleDates,
        };
      });
      return { productCategory };
    };

    const updateData = {
      data: {
        primary: buildType("primary"),
        secondary: buildType("secondary"),
      },
    };

    await mutateAsync({
      patientId: frequencyParams.patientId,
      assessmentId: frequencyParams.assessmentId,
      interventionId,
      data: updateData.data,
    });
    await refetch();
    if (interventionId === 0) {
      await dispatch(getInterventionList(frequencyParams));
    }
    await dispatch(getMinimumDatasetInfo(frequencyParams.assessmentId))
    setEditable(false);
    setFormChangesConfirm(false);
    setChangedScheduleKeys(new Set());
    setDraftScheduleDates({});
  }, [changedScheduleKeys, dispatch, draftScheduleDates, interventionId, dressChange, frequencyParams, mutateAsync, refetch, schedules]);

  useEffect(() => {
    if (!editable) return;
    if (formChangesConfirm && clickCancel) {
      setConfirmationModal(true);
    } else if (clickCancel && !formChangesConfirm) {
      setClickCancel(false);
      setConfirmationModal(false);
      setEditable(false);
    }
  }, [formChangesConfirm, clickCancel, editable]);

  useEffect(() => {
    toast.dismiss("success1");
  }, [assessmentId, interventionId, patientId]);

  useEffect(() => {
    if (!editable) {
      setSchedules(buildSchedules(dressChange));
      setDraftScheduleDates({});
    }
  }, [dressChange, editable]);

  return {
    editable,
    confirmationModal,
    isLoading,
    isRefreshing,
    isSaving,
    dressChange,
    isReadOnly,
    hasPermission,
    handleSubmit,
    handleEdit,
    handleCancel,
    doSubmit,
    confirmationClose,
    yesConfirmationModalClick,
    setFormChangesConfirm,
    getSchedule,
    getDraftScheduleDates,
    updateSchedule,
    toggleWeekday,
  };
};
